<script setup lang="ts">
// Small glyph for a sub-category, in the same line style as CategoryArt
// (ink outline, round joins, one flat accent) on its parent category's tint.
// Drawn on a 48×48 grid so it stays crisp at chip size (28–40px).
import { computed } from 'vue'
import { paletteFor, type ArtTone } from '@/data/categoryArtPalettes'
import { useThemeStore } from '@/stores/theme'

const props = defineProps<{ slug: string; tone?: ArtTone }>()
const themeStore = useThemeStore()
const p = computed(() => paletteFor(props.slug, props.tone || {
  ink: themeStore.theme.inkColor || '#14171F',
  ground: themeStore.theme.backgroundColor || '#F7F6F2',
}))
</script>

<template>
  <span class="inline-flex shrink-0 items-center justify-center overflow-hidden rounded-lg" :style="{ background: p.bg }" aria-hidden="true">
    <svg class="h-[82%] w-[82%]" viewBox="0 0 48 48" fill="none" :stroke="p.ink" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
      <!-- Electronics -->
      <template v-if="slug === 'phones'">
        <rect x="21" y="13" width="18" height="25" rx="3" :fill="p.accent" />
        <rect x="11" y="9" width="14" height="28" rx="3" :fill="p.soft" />
        <path d="M16 12 H20" />
      </template>
      <template v-else-if="slug === 'laptops'">
        <rect x="11" y="12" width="26" height="18" rx="2" :fill="p.accent" />
        <path d="M6 34 H42 L39 38 H9 Z" :fill="p.soft" />
      </template>
      <template v-else-if="slug === 'appliances'">
        <rect x="12" y="8" width="24" height="32" rx="3" :fill="p.soft" />
        <circle cx="24" cy="26" r="7.5" :fill="p.accent" />
        <path d="M16 13 H22" />
        <circle cx="31" cy="13" r="1.2" :fill="p.ink" stroke="none" />
      </template>

      <!-- Vehicles -->
      <template v-else-if="slug === 'sedans'">
        <path d="M6 31 H42 V26 a4 4 0 0 0 -3 -4 L33 20 L28 14 H18 L12 20 L9 21 a4 4 0 0 0 -3 4 Z" :fill="p.accent" />
        <circle cx="15" cy="32" r="4" :fill="p.ink" />
        <circle cx="33" cy="32" r="4" :fill="p.ink" />
      </template>
      <template v-else-if="slug === 'suvs'">
        <path d="M6 32 H42 V23 a3 3 0 0 0 -3 -3 H36 L32 12 H15 a3 3 0 0 0 -3 2 L9 20 a3 3 0 0 0 -3 3 Z" :fill="p.accent" />
        <path d="M16 20 L18 15 H24 V20 Z M28 15 H31 L33 20 H28 Z" :fill="p.soft" />
        <circle cx="15" cy="33" r="5" :fill="p.ink" />
        <circle cx="34" cy="33" r="5" :fill="p.ink" />
      </template>
      <template v-else-if="slug === 'commercial_transport'">
        <rect x="5" y="13" width="23" height="18" rx="1.5" :fill="p.soft" />
        <path d="M28 19 H36 L42 26 V31 H28 Z" :fill="p.accent" />
        <circle cx="13" cy="33" r="4" :fill="p.ink" />
        <circle cx="35" cy="33" r="4" :fill="p.ink" />
      </template>
      <template v-else-if="slug === 'moto'">
        <circle cx="12" cy="32" r="6" />
        <circle cx="36" cy="32" r="6" />
        <path d="M21 23 q5 -6 11 0 Z" :fill="p.accent" />
        <path d="M12 32 L20 23 H32 L36 32 M32 23 L34 15 H38 M20 23 L17 18 H13" />
      </template>

      <!-- Real estate -->
      <template v-else-if="slug === 'commercial'">
        <rect x="10" y="20" width="28" height="19" :fill="p.soft" />
        <path d="M7 20 L11 11 H37 L41 20 Z" :fill="p.accent" />
        <path d="M17 11 L15 20 M24 11 V20 M31 11 L33 20" stroke-width="1.8" />
        <rect x="20" y="28" width="8" height="11" :fill="p.accent" />
      </template>
      <template v-else-if="slug === 'apartments'">
        <rect x="14" y="7" width="20" height="33" rx="1.5" :fill="p.soft" />
        <path d="M18 12 h4 M26 12 h4 M18 18 h4 M26 18 h4 M18 24 h4 M26 24 h4" stroke-width="3" :stroke="p.accent" />
        <rect x="21" y="31" width="6" height="9" :fill="p.accent" />
      </template>
      <template v-else-if="slug === 'houses'">
        <rect x="13" y="21" width="22" height="18" :fill="p.soft" />
        <path d="M9 23 L24 10 L39 23" :fill="p.accent" />
        <rect x="21" y="29" width="6" height="10" :fill="p.accent" />
      </template>
      <template v-else-if="slug === 'lands'">
        <path d="M5 38 q9 -9 19 -2 q9 -8 19 0 V40 H5 Z" :fill="p.accent" />
        <path d="M30 32 V12 L39 15.5 L30 19" :fill="p.soft" />
        <path d="M9 30 h4 M15 26 h3" stroke-width="1.8" />
      </template>

      <!-- Livestock -->
      <template v-else-if="slug === 'cows'">
        <path d="M16 15 q-7 -1 -8 -8 M32 15 q7 -1 8 -8" />
        <path d="M15 14 H33 V29 a9 9 0 0 1 -18 0 Z" :fill="p.soft" />
        <ellipse cx="24" cy="33" rx="7" ry="5" :fill="p.accent" />
        <circle cx="19" cy="21" r="1.4" :fill="p.ink" stroke="none" />
        <circle cx="29" cy="21" r="1.4" :fill="p.ink" stroke="none" />
      </template>
      <template v-else-if="slug === 'horses'">
        <path d="M17 41 V27 q0 -12 11 -16 l2 -5 2 5 q7 4 7 13 l-3 3 -6 -2 -4 4 V41 Z" :fill="p.accent" />
        <path d="M28 11 q-6 2 -8 9" stroke-width="1.8" />
        <circle cx="32" cy="17" r="1.4" :fill="p.ink" stroke="none" />
      </template>
      <template v-else-if="slug === 'sheep'">
        <path d="M17 33 V40 M31 33 V40" stroke-width="3" />
        <path d="M12 30 q-5 -1 -4 -6 q-1 -6 5 -7 q1 -6 8 -5 q4 -4 9 -2 q6 -1 8 4 q5 1 4 6 q3 5 -2 8 q0 5 -6 4 q-3 3 -8 2 q-5 2 -9 -1 q-6 1 -5 -3 Z" :fill="p.soft" />
        <ellipse cx="40" cy="21" rx="4.5" ry="6" :fill="p.ink" />
      </template>
      <template v-else-if="slug === 'poultry'">
        <path d="M10 25 L5 18 q6 0 8 4" :fill="p.accent" />
        <path d="M33 16 a5 5 0 1 0 -9 2 L23 22 q-11 -4 -13 5 q-1 10 11 12 h6 q11 -2 11 -13 q0 -5 -5 -10 Z" :fill="p.soft" />
        <path d="M27 9 q0 -3 3 -3 q2 -2 4 1 q2 1 0 3" :fill="p.ink" />
        <path d="M33 13 L38 15 L33 17" :fill="p.ink" stroke-width="1.6" />
        <circle cx="30" cy="13" r="1.1" :fill="p.ink" stroke="none" />
        <path d="M19 39 V43 M26 39 V43" />
        <path d="M16 28 q5 4 11 0" stroke-width="1.8" />
      </template>
      <template v-else-if="slug === 'feed'">
        <rect x="8" y="17" width="32" height="20" rx="5" :fill="p.accent" />
        <path d="M17 17 V37 M31 17 V37" />
        <path d="M11 17 L9 12 M20 17 L21 11 M28 17 L27 12 M37 17 L39 12" stroke-width="1.8" />
      </template>

      <!-- Jewelry -->
      <template v-else-if="slug === 'gold'">
        <path d="M7 38 L11 29 H25 L29 38 Z" :fill="p.accent" />
        <path d="M19 29 L23 20 H37 L41 29 H25" :fill="p.soft" />
        <path d="M31 9 H39 L41 12 L35 18 L29 12 Z" :fill="p.accent" stroke-width="1.8" />
      </template>
      <template v-else-if="slug === 'watches'">
        <rect x="18" y="5" width="12" height="9" rx="2" :fill="p.accent" />
        <rect x="18" y="34" width="12" height="9" rx="2" :fill="p.accent" />
        <circle cx="24" cy="24" r="10" :fill="p.soft" />
        <path d="M24 18 V24 L28 27" />
      </template>
      <template v-else-if="slug === 'national'">
        <circle cx="24" cy="9" r="3" />
        <path d="M24 12 V15" />
        <path d="M13 17 q11 20 22 0 q-11 9 -22 0 Z" :fill="p.accent" />
        <path d="M17 29 V38 M24 31 V41 M31 29 V38" stroke-width="1.8" />
        <circle cx="17" cy="39" r="1.6" :fill="p.ink" stroke="none" />
        <circle cx="24" cy="42" r="1.6" :fill="p.ink" stroke="none" />
        <circle cx="31" cy="39" r="1.6" :fill="p.ink" stroke="none" />
      </template>

      <!-- Art & antiques -->
      <template v-else-if="slug === 'shyrdak'">
        <rect x="7" y="11" width="34" height="26" rx="2" :fill="p.accent" />
        <path d="M24 31 V25 C24 20 19 19 17 21 C15 23 16 26 18 25 M24 25 C24 20 29 19 31 21 C33 23 32 26 30 25" :stroke="p.soft" stroke-width="2" />
        <path d="M11 15 H37" :stroke="p.soft" stroke-width="1.6" stroke-dasharray="3 3" />
      </template>
      <template v-else-if="slug === 'coins'">
        <ellipse cx="20" cy="33" rx="11" ry="4" :fill="p.accent" />
        <path d="M9 33 V28 M31 33 V28" />
        <ellipse cx="20" cy="28" rx="11" ry="4" :fill="p.soft" />
        <circle cx="32" cy="18" r="9" :fill="p.accent" />
        <path d="M32 14 V22" stroke-width="2" />
      </template>
      <template v-else-if="slug === 'paintings'">
        <rect x="8" y="10" width="32" height="26" rx="2" :fill="p.soft" />
        <path d="M11 33 L19 22 L24 28 L29 20 L37 33 Z" :fill="p.accent" />
        <circle cx="31" cy="16" r="2.5" :fill="p.accent" />
        <path d="M18 42 L21 36 M30 42 L27 36" />
      </template>

      <!-- Machinery -->
      <template v-else-if="slug === 'tractors'">
        <path d="M24 12 H35 L37 26 H24 Z" :fill="p.soft" />
        <rect x="8" y="24" width="20" height="9" rx="2" :fill="p.accent" />
        <path d="M13 24 V16" stroke-width="3" />
        <circle cx="32" cy="34" r="7.5" :fill="p.ink" />
        <circle cx="13" cy="37" r="4.5" :fill="p.ink" />
      </template>
      <template v-else-if="slug === 'construction'">
        <rect x="6" y="33" width="24" height="7" rx="3.5" :fill="p.ink" />
        <path d="M9 33 V24 H21 L24 28 V33 Z" :fill="p.accent" />
        <path d="M22 26 L32 13 L40 21" stroke-width="3" />
        <path d="M38 20 L43 25 L39 30 L35 26 Z" :fill="p.soft" />
      </template>
      <template v-else-if="slug === 'attachments'">
        <path d="M6 14 H42" stroke-width="3" />
        <path d="M13 14 V22 q0 10 -6 14 M24 14 V22 q0 10 -6 14 M35 14 V22 q0 10 -6 14" :stroke="p.ink" />
        <path d="M13 22 q0 10 -6 14 L12 36 q5 -5 5 -14 Z M24 22 q0 10 -6 14 L23 36 q5 -5 5 -14 Z M35 22 q0 10 -6 14 L34 36 q5 -5 5 -14 Z" :fill="p.accent" stroke-width="1.6" />
      </template>

      <!-- Home goods -->
      <template v-else-if="slug === 'living-room'">
        <rect x="14" y="13" width="20" height="16" rx="5" :fill="p.accent" />
        <rect x="12" y="25" width="24" height="9" rx="3" :fill="p.soft" />
        <rect x="8" y="21" width="7" height="14" rx="3" :fill="p.accent" />
        <rect x="33" y="21" width="7" height="14" rx="3" :fill="p.accent" />
        <path d="M13 35 V40 M35 35 V40" stroke-width="3" />
      </template>
      <template v-else-if="slug === 'bedroom'">
        <path d="M7 38 V14 M41 38 V27" stroke-width="3" />
        <rect x="7" y="27" width="34" height="8" rx="1.5" :fill="p.accent" />
        <rect x="10" y="20" width="11" height="7" rx="3" :fill="p.soft" />
        <path d="M7 31 H41" stroke-width="1.6" />
      </template>
      <template v-else-if="slug === 'kitchen-dining'">
        <path d="M9 22 H39" stroke-width="3" />
        <path d="M13 22 V40 M35 22 V40" stroke-width="3" />
        <ellipse cx="24" cy="18" rx="9" ry="3" :fill="p.soft" />
        <path d="M20 14 q-1 -4 1 -6 M26 14 q-1 -4 1 -6" stroke-width="1.8" />
      </template>
      <template v-else-if="slug === 'home-decor'">
        <path d="M15 20 L19 7 H29 L33 20 Z" :fill="p.accent" />
        <path d="M24 20 V36" stroke-width="2.6" />
        <path d="M16 40 q8 -6 16 0 Z" :fill="p.soft" />
      </template>

      <template v-else>
        <rect x="11" y="20" width="26" height="18" rx="3" :fill="p.accent" />
        <rect x="16" y="10" width="16" height="11" rx="3" :fill="p.soft" />
      </template>
    </svg>
  </span>
</template>
